import React, { useState, cloneElement } from "react";
import { View, Text } from "react-native";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { useTranslation } from "react-i18next";
import { useDevice } from "../_layout";
import Colors from "../../Resources/Color";

/**
 * Tooltip Component
 *
 * A reusable tooltip component that displays helpful information on hover (desktop) or tap (mobile).
 * Uses @floating-ui for intelligent positioning that automatically adjusts based on available space.
 *
 * @param {React.ReactNode} children - The element that triggers the tooltip
 * @param {string} content - The localization key (e.g., "macrocycleTooltip-string") or pre-translated text for special cases
 * @param {string} placement - Preferred placement: 'top', 'bottom', 'left', 'right' (default: 'top')
 * @param {number} offsetDistance - Distance from the trigger element (default: 8)
 * @param {number} maxWidth - Maximum width of the tooltip (default: 280)
 * @param {boolean} disabled - Whether the tooltip is disabled (default: false)
 * @param {boolean} delayedOpen - Whether to delay showing the tooltip by 2 seconds (default: false)
 *
 * @example
 * <Tooltip content="macrocycleTooltip-string" placement="top">
 *   <View style={styles.tooltipIconContainer}>
 *     <Ionicons name="information-circle-outline" size={18} />
 *   </View>
 * </Tooltip>
 */
const Tooltip = ({
  children,
  content,
  placement = "top",
  offsetDistance = 8,
  maxWidth = 280,
  disabled = false,
  delayedOpen = false,
  forceOpen = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { isMobile } = useDevice();
  const { t } = useTranslation();

  const open = forceOpen || isOpen;

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(offsetDistance),
      flip({
        fallbackAxisSideDirection: "start",
        padding: 8,
      }),
      shift({ padding: 8 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, {
    enabled: !isMobile && !disabled,
    delay: { open: delayedOpen ? 1000 : 200, close: 0 },
  });

  const click = useClick(context, {
    enabled: isMobile && !disabled,
  });

  const focus = useFocus(context, {
    enabled: !disabled,
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "tooltip" });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    click,
    focus,
    dismiss,
    role,
  ]);

  // Don't render if disabled or no content
  if (disabled || !content) {
    return children;
  }

  return (
    <>
      {cloneElement(
        children,
        getReferenceProps({
          ref: refs.setReference,
          ...children.props,
        })
      )}

      {open && (
        <FloatingPortal>
          <View
            ref={refs.setFloating}
            style={[
              {
                position: "fixed",
                zIndex: 120000,
                backgroundColor: Colors.blackColor,
                paddingVertical: 8,
                paddingHorizontal: 12,
                borderRadius: 8,
                shadowColor: "#000",
                shadowOffset: {
                  width: 0,
                  height: 4,
                },
                shadowOpacity: 0.3,
                shadowRadius: 8,
                maxWidth,
              },
              floatingStyles,
            ]}
            {...getFloatingProps()}
          >
            <Text
              style={{
                color: Colors.whiteColor,
                fontSize: 13,
                lineHeight: 18,
              }}
            >
              {t(content)}
            </Text>
          </View>
        </FloatingPortal>
      )}
    </>
  );
};

export default Tooltip;
